import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Skeleton,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EmptyState from "./EmptyState";

/**
 * One data set, two presentations.
 *
 * Desktop renders a table. Below `md` it renders a tap-able card list, which is
 * what makes list screens usable on a phone — a horizontally scrolling table is
 * not.
 *
 * Column options beyond the usual `field` / `headerName` / `renderCell`:
 *   primary:      use as the card title on mobile
 *   secondary:    use as the card subtitle on mobile
 *   badge:        render top-right of the card (status chips etc.)
 *   hideOnMobile: omit from the card body entirely
 *   sortable:     enable the header sort toggle
 *
 * Pagination is opt-in via `pageSize`; without it every row renders, which is
 * what the short lists on most screens want.
 */

const getValue = (row, column) => {
  if (column.renderCell) return column.renderCell({ row, value: row[column.field] });
  const value = row[column.field];
  if (value === null || value === undefined || value === "") return "—";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
  return value;
};

const compare = (a, b, field) => {
  const av = a?.[field];
  const bv = b?.[field];
  if (av === bv) return 0;
  if (av === null || av === undefined) return 1;
  if (bv === null || bv === undefined) return -1;
  if (typeof av === "number" && typeof bv === "number") return av - bv;
  return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" });
};

function RowActions({ actions, row }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const items = typeof actions === "function" ? actions(row) : actions;
  if (!items?.length) return null;

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
        aria-label="Row actions"
      >
        <MoreVertIcon sx={{ fontSize: 19 }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={(e) => {
          e?.stopPropagation?.();
          setAnchorEl(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { minWidth: 168 } } }}
      >
        {items.map((item, i) =>
          item.divider ? (
            <Divider key={`divider-${i}`} sx={{ my: 0.5 }} />
          ) : (
            <MenuItem
              key={item.label}
              disabled={item.disabled}
              onClick={(e) => {
                e.stopPropagation();
                setAnchorEl(null);
                item.onClick?.(row);
              }}
              sx={item.danger ? { color: "error.main" } : undefined}
            >
              {item.icon && (
                <ListItemIcon>
                  <item.icon
                    sx={{ fontSize: 18, color: item.danger ? "error.main" : undefined }}
                  />
                </ListItemIcon>
              )}
              {item.label}
            </MenuItem>
          )
        )}
      </Menu>
    </>
  );
}

function MobileCards({ columns, rows, getRowId, onRowClick, actions }) {
  const titleCol = columns.find((c) => c.primary) || columns[0];
  const subtitleCol = columns.find((c) => c.secondary);
  const badgeCol = columns.find((c) => c.badge);
  const bodyCols = columns.filter(
    (c) => c !== titleCol && c !== subtitleCol && c !== badgeCol && !c.hideOnMobile
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
      {rows.map((row) => (
        <Card
          key={getRowId(row)}
          onClick={onRowClick ? () => onRowClick(row) : undefined}
          sx={{
            p: 2,
            ...(onRowClick && {
              cursor: "pointer",
              transition: "background-color .15s ease",
              "&:active": { bgcolor: "action.hover" },
            }),
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                {getValue(row, titleCol)}
              </Typography>
              {subtitleCol && (
                <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 0.25 }}>
                  {getValue(row, subtitleCol)}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
              {badgeCol && getValue(row, badgeCol)}
              {actions && <RowActions actions={actions} row={row} />}
              {onRowClick && !actions && (
                <ChevronRightIcon sx={{ fontSize: 20, color: "text.disabled" }} />
              )}
            </Box>
          </Box>

          {bodyCols.length > 0 && (
            <Box
              sx={{
                mt: 1.5,
                pt: 1.5,
                borderTop: 1,
                borderColor: "border.subtle",
                display: "grid",
                gridTemplateColumns: "auto 1fr",
                rowGap: 0.75,
                columnGap: 2,
                alignItems: "baseline",
              }}
            >
              {bodyCols.map((col) => (
                <React.Fragment key={col.field}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {col.headerName}
                  </Typography>
                  <Box sx={{ fontSize: "0.875rem", textAlign: "right", minWidth: 0 }}>
                    {getValue(row, col)}
                  </Box>
                </React.Fragment>
              ))}
            </Box>
          )}
        </Card>
      ))}
    </Box>
  );
}

export default function DataView({
  columns = [],
  rows = [],
  loading = false,
  getRowId = (row) => row.id,
  onRowClick,
  actions,
  empty,
  defaultSort,
  variant, // "table" | "cards" — omit to switch automatically at `md`
  toolbar,
  pageSize, // rows per page; omit to render every row
  rowsPerPageOptions = [10, 25, 50],
}) {
  const [sort, setSort] = useState(defaultSort || null);

  const sortedRows = useMemo(() => {
    if (!sort?.field) return rows;
    const sorted = [...rows].sort((a, b) => compare(a, b, sort.field));
    return sort.direction === "desc" ? sorted.reverse() : sorted;
  }, [rows, sort]);

  const paginated = Number(pageSize) > 0;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(Number(pageSize) || 25);
  const pageCount = Math.max(1, Math.ceil(sortedRows.length / rowsPerPage));

  // Searching or filtering can shrink the list out from under the current
  // page. Fall back to the last page that still has rows, not an empty one.
  useEffect(() => {
    if (page > pageCount - 1) setPage(pageCount - 1);
  }, [page, pageCount]);

  const pageRows = useMemo(() => {
    if (!paginated) return sortedRows;
    const start = Math.min(page, pageCount - 1) * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [paginated, sortedRows, page, pageCount, rowsPerPage]);

  const handleSort = (field) => {
    setSort((prev) =>
      prev?.field === field
        ? { field, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { field, direction: "asc" }
    );
  };

  const forceCards = variant === "cards";
  const forceTable = variant === "table";

  if (loading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} variant="rounded" height={64} />
        ))}
      </Box>
    );
  }

  if (!sortedRows.length) {
    return empty || <EmptyState />;
  }

  const pagination = paginated ? (
    <TablePagination
      component="div"
      count={sortedRows.length}
      page={Math.min(page, pageCount - 1)}
      onPageChange={(_, next) => setPage(next)}
      rowsPerPage={rowsPerPage}
      rowsPerPageOptions={rowsPerPageOptions}
      onRowsPerPageChange={(event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
      }}
      labelRowsPerPage="Rows"
      sx={{
        // The default toolbar is wider than a phone, so let it wrap rather
        // than make the card scroll sideways.
        ".MuiTablePagination-toolbar": { flexWrap: "wrap", rowGap: 0.5, px: 1 },
      }}
    />
  ) : null;

  const cardView = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
      <MobileCards
        columns={columns}
        rows={pageRows}
        getRowId={getRowId}
        onRowClick={onRowClick}
        actions={actions}
      />
      {pagination && <Card>{pagination}</Card>}
    </Box>
  );

  const tableView = (
    <Card sx={{ overflow: "hidden" }}>
      {toolbar}
      <TableContainer sx={{ borderRadius: 0 }}>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.field} align={col.align} sx={{ width: col.width }}>
                  {col.sortable ? (
                    <TableSortLabel
                      active={sort?.field === col.field}
                      direction={sort?.field === col.field ? sort.direction : "asc"}
                      onClick={() => handleSort(col.field)}
                    >
                      {col.headerName}
                    </TableSortLabel>
                  ) : (
                    col.headerName
                  )}
                </TableCell>
              ))}
              {actions && <TableCell align="right" sx={{ width: 56 }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.map((row) => (
              <TableRow
                key={getRowId(row)}
                hover={Boolean(onRowClick)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                sx={onRowClick ? { cursor: "pointer" } : undefined}
              >
                {columns.map((col) => (
                  <TableCell key={col.field} align={col.align}>
                    {getValue(row, col)}
                  </TableCell>
                ))}
                {actions && (
                  <TableCell align="right" padding="none" sx={{ pr: 1 }}>
                    <RowActions actions={actions} row={row} />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination}
    </Card>
  );

  if (forceCards) return cardView;
  if (forceTable) return tableView;

  // Both trees render; CSS decides. Avoids a layout flash on first paint that a
  // JS-only media query would cause.
  return (
    <>
      <Box sx={{ display: { xs: "block", md: "none" } }}>{cardView}</Box>
      <Box sx={{ display: { xs: "none", md: "block" } }}>{tableView}</Box>
    </>
  );
}
